import React, { useState, useEffect } from 'react';
import { Wrench, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';

export interface ToolCall {
  id: string;
  tool_name: string;
  parameters: Record<string, unknown>;
  is_critical: boolean;
  status: 'pending' | 'executing' | 'completed' | 'error';
  timestamp: Date;
}

interface ToolCallIndicatorProps {
  toolCalls: ToolCall[];
  isStreaming: boolean;
}

/**
 * ToolCallIndicator - Shows active tool calls during agent execution
 *
 * Displays a collapsible list of tools being called by the agent,
 * with visual indicators for critical operations and execution status.
 */
const ToolCallIndicator: React.FC<ToolCallIndicatorProps> = ({
  toolCalls,
  isStreaming,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Auto-collapse after streaming ends
  useEffect(() => {
    if (!isStreaming && toolCalls.length > 0) {
      const timer = setTimeout(() => setIsExpanded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, toolCalls.length]);

  // Don't render if no tool calls or not streaming
  if (toolCalls.length === 0) {
    return null;
  }

  const activeTools = toolCalls.filter(t => t.status === 'executing' || t.status === 'pending');
  const completedTools = toolCalls.filter(t => t.status === 'completed');
  const criticalTools = toolCalls.filter(t => t.is_critical);

  // Format tool name for display
  const formatToolName = (name: string): string => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get status icon
  const getStatusIcon = (status: ToolCall['status'], isCritical: boolean) => {
    if (isCritical) {
      return <AlertTriangle size={14} className="tool-call-icon-critical" />;
    }
    switch (status) {
      case 'executing':
      case 'pending':
        return <Loader2 size={14} className="tool-call-icon-loading" />;
      case 'completed':
        return <CheckCircle2 size={14} className="tool-call-icon-success" />;
      case 'error':
        return <AlertTriangle size={14} className="tool-call-icon-error" />;
      default:
        return <Wrench size={14} className="tool-call-icon-default" />;
    }
  };

  return (
    <div className="tool-call-indicator" role="status" aria-live="polite">
      <button
        className="tool-call-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="tool-call-list"
      >
        <div className="tool-call-header-left">
          <Wrench size={16} className="tool-call-header-icon" />
          <span className="tool-call-header-text">
            {isStreaming ? (
              <>
                <span className="tool-call-count">{activeTools.length}</span> tool{activeTools.length !== 1 ? 's' : ''} executing
                {criticalTools.length > 0 && (
                  <span className="tool-call-critical-badge">
                    {criticalTools.length} critical
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="tool-call-count">{completedTools.length}</span> tool{completedTools.length !== 1 ? 's' : ''} completed
              </>
            )}
          </span>
        </div>
        <span className="tool-call-expand-icon">
          {isExpanded ? '\u25B2' : '\u25BC'}
        </span>
      </button>

      {isExpanded && (
        <ul id="tool-call-list" className="tool-call-list">
          {toolCalls.map((tool) => (
            <li
              key={tool.id}
              className={`tool-call-item ${tool.is_critical ? 'tool-call-item-critical' : ''}`}
            >
              <div className="tool-call-item-header">
                {getStatusIcon(tool.status, tool.is_critical)}
                <span className="tool-call-name">{formatToolName(tool.tool_name)}</span>
                {tool.is_critical && (
                  <span className="tool-call-critical-tag">Critical</span>
                )}
              </div>
              {Object.keys(tool.parameters).length > 0 && (
                <div className="tool-call-params">
                  {Object.entries(tool.parameters).slice(0, 3).map(([key, value]) => (
                    <span key={key} className="tool-call-param">
                      <span className="tool-call-param-key">{key}:</span>
                      <span className="tool-call-param-value">
                        {typeof value === 'string'
                          ? value.length > 30 ? value.slice(0, 30) + '...' : value
                          : JSON.stringify(value).slice(0, 30)
                        }
                      </span>
                    </span>
                  ))}
                  {Object.keys(tool.parameters).length > 3 && (
                    <span className="tool-call-param-more">
                      +{Object.keys(tool.parameters).length - 3} more
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ToolCallIndicator;
